/**
 * Education Section Component
 * Displays candidate's education history with inline edit capability
 * Matches Figma design from My Profile page
 */

import React, { useState, useEffect } from 'react';
import { GraduationCap, PenSquare, Plus, Trash2, X, Check } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

interface Education {
    degree: string;
    institution: string;
    fieldOfStudy?: string;
    startDate: string;
    endDate: string;
}

interface EducationSectionProps {
    profile: CandidateProfile;
    onSave?: (education: Education[]) => Promise<void>;
}

const EducationSection: React.FC<EducationSectionProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editedEducation, setEditedEducation] = useState<Education[]>([]);

    // Get education from profile
    // Priority: structured field (source of truth) > legacy fallbacks
    const education = profile?.parsedResume?.education ||
                     profile?.parsedResume?.rawJson?.education?.education ||
                     profile?.resume_data?.parsed_data?.education?.education ||
                     [];

    // Track dirty state for save button
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(editedEducation);

    // Initialize edited education when entering edit mode
    useEffect(() => {
        if (isEditing) {
            const initialEducation = education.map((edu: any) => ({
                degree: edu.degree || edu.degree_name || '',
                institution: edu.institution || edu.school_name || '',
                fieldOfStudy: edu.fieldOfStudy || edu.field_of_study || '',
                startDate: edu.startDate || edu.start_date || edu.startYear || edu.start_year || '',
                endDate: edu.endDate || edu.end_date || edu.endYear || edu.end_year || ''
            }));
            setEditedEducation(initialEducation);
            setOriginal(initialEducation);
        } else {
            resetDirty();
        }
    }, [isEditing]);

    // Format date range
    const formatDateRange = (startDate: string, endDate: string): string => {
        if (!startDate && !endDate) return '';

        const start = startDate || '';
        const end = endDate || 'Present';

        if (!start) return end;
        return `${start} - ${end}`;
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedEducation([]);
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(editedEducation);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save education:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleAddEducation = () => {
        setEditedEducation([
            ...editedEducation,
            { degree: '', institution: '', fieldOfStudy: '', startDate: '', endDate: '' }
        ]);
    };

    const handleRemoveEducation = (index: number) => {
        setEditedEducation(editedEducation.filter((_, i) => i !== index));
    };

    const handleEducationChange = (index: number, field: keyof Education, value: string) => {
        const updated = [...editedEducation];
        updated[index] = { ...updated[index], [field]: value };
        setEditedEducation(updated);
    };

    // Show message if no education and not editing
    if (education.length === 0 && !isEditing) {
        return (
            <div className="bg-white rounded-2xl p-6 w-full border border-[#EDEDED]">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <GraduationCap size={20} className="text-[#0B111E]" />
                        <h3
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '18px',
                                lineHeight: '25px',
                                color: '#0B111E',
                            }}
                        >
                            Education
                        </h3>
                    </div>
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <Plus size={20} />
                    </button>
                </div>
                <p className="text-sm text-gray-500">No education added yet.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                    <GraduationCap size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">Education</h3>
                </div>
                {!isEditing ? (
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <PenSquare size={20} />
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            disabled={isSaving}
                        >
                            <X size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`p-2 rounded-full transition-colors ${
                                isDirty ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                            }`}
                            disabled={isSaving || !isDirty}
                        >
                            <Check size={20} />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    {editedEducation.map((edu, index) => (
                        <div key={index} className="p-4 border border-[#EDEDED] rounded-lg bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                                <span className="text-sm font-medium text-gray-600">Education {index + 1}</span>
                                <button
                                    onClick={() => handleRemoveEducation(index)}
                                    className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Degree</label>
                                    <input
                                        type="text"
                                        value={edu.degree}
                                        onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                                        placeholder="e.g. Bachelor of Science in Computer Science"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">School/University</label>
                                    <input
                                        type="text"
                                        value={edu.institution}
                                        onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                                        placeholder="e.g. Stanford University"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                                    <input
                                        type="month"
                                        value={edu.startDate}
                                        onChange={(e) => handleEducationChange(index, 'startDate', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                                    <input
                                        type="month"
                                        value={edu.endDate}
                                        onChange={(e) => handleEducationChange(index, 'endDate', e.target.value)}
                                        placeholder="Present"
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={handleAddEducation}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#015FA6] hover:text-[#015FA6] transition-colors flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Add Education
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {education.map((edu: any, index: number) => {
                        const degree = edu.degree || edu.degree_name || '';
                        const fieldOfStudy = edu.fieldOfStudy || edu.field_of_study || edu.major || '';
                        const school = edu.institution || edu.school_name || edu.school || '';
                        const dateRange = formatDateRange(
                            edu.startDate || edu.start_date || edu.startYear || edu.start_year,
                            edu.endDate || edu.end_date || edu.endYear || edu.end_year
                        );

                        // Build degree display: "Degree in Field" or just "Degree"
                        const degreeDisplay = fieldOfStudy && degree
                            ? `${degree} in ${fieldOfStudy}`
                            : (degree || fieldOfStudy || 'Degree not specified');

                        // Build school line: "School | Dates" or just "School" or just "Dates"
                        const schoolDisplay = school || 'School not specified';
                        const secondLine = dateRange ? `${schoolDisplay} | ${dateRange}` : schoolDisplay;

                        return (
                            <div key={index} className="p-3 border border-[#F4F4F4] rounded-lg hover:border-[#015FA6]/30 transition-colors">
                                <h4 className="text-sm font-medium text-[#515151] mb-1">{degreeDisplay}</h4>
                                <p className="text-sm text-[#757575]">{secondLine}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default EducationSection;
